import React, { useMemo, useState } from 'react';
import { Users } from 'lucide-react';
import { useUser } from '../../context/UserContext';
import { usePostApi } from '../../hooks/post_api_hook/usePostApi';
import { url } from '../../utils/confserveur';
import { grouperParClasses, lignesPourMoteur } from '../../utils/importEtablissement';
import EtapeFichier from './EtapeFichier';
import EtapeClasses from './EtapeClasses';
import EtapeRapport from './EtapeRapport';
import EtapeResultat from './EtapeResultat';

/**
 * Wizard d'import UNIQUE de l'établissement (spec §10) — tous régimes.
 * fichier → classes (périmètre + licence) → rapport (dry-run) → resultat (apply).
 *
 * Le back est stateless : l'apply renvoie les MÊMES lignes que le dry-run
 * (figées dans lignesEnvoyees) plus les résolutions. Un refus serveur (delta
 * sortants, limite...) s'affiche tel quel — « Refaire l'analyse » relance le
 * dry-run pour repartir d'un rapport à jour.
 *
 * Flux suspects/conflits (confirmation stricte des sortants) :
 *   dry-run initial → réponses du prof (rapport PÉRIMÉ) → RE-dry-run avec
 *   suspects_resolus/conflits_resolus → sortants rafraîchis re-confirmés →
 *   apply (mêmes résolutions + sortants_confirmes du DERNIER dry-run).
 * Sans le re-dry-run, un « deux_eleves » crée un sortant absent du rapport
 * initial et le back refuse l'apply (« sortant(s) non confirmé(s) »).
 *
 * Les DÉPLACEMENTS empruntent le même chemin (deplacements_resolus) : un
 * changement de classe détecté par le nom peut être un élève parti remplacé
 * par un homonyme au même niveau, le fichier est identique dans les deux cas
 * et seul le référent le sait. C'est un cas à confirmer comme un suspect :
 * 'meme' (répondu, rien à envoyer : le déplacement est le défaut du moteur)
 * ou 'creer' (envoyé ; au re-dry-run la ligne passe dans les créés et
 * l'ancienne fiche dans les sortants, à confirmer comme les autres). Seuls
 * les 'creer' partent au back et comptent pour « rapport périmé ».
 */

/** Ce qui part au moteur : les seules lignes déclarées « nouvel élève ». */
const creerSeulement = (dict) => Object.fromEntries(
    Object.entries(dict || {}).filter(([, v]) => v === 'creer'));

const ETAPES = [
    { cle: 'fichier', titre: 'Fichier' },
    { cle: 'classes', titre: 'Classes' },
    { cle: 'rapport', titre: 'Vérification' },
    { cle: 'resultat', titre: 'Terminé' },
];

function ImportWizardEtablissement() {
    const { etablissementData } = useUser();
    const [etape, setEtape] = useState('fichier');
    const [groupes, setGroupes] = useState([]);
    const [cochees, setCochees] = useState(new Set());
    const [sansClasse, setSansClasse] = useState(0);   // lignes du fichier sans classe, ignorées
    const [lignesEnvoyees, setLignesEnvoyees] = useState([]);
    const [rapport, setRapport] = useState(null);
    const [rapportFinal, setRapportFinal] = useState(null);
    const [echec, setEchec] = useState(null);   // message d'échec de l'apply (écran de résultat rouge)
    const [resolutionsConflits, setResolutionsConflits] = useState({});
    const [resolutionsSuspects, setResolutionsSuspects] = useState({});
    // { [String(ligne)]: 'meme' | 'creer' } ; clé absente = pas encore répondu
    const [resolutionsDeplacements, setResolutionsDeplacements] = useState({});
    // Résolutions déjà INTÉGRÉES au rapport affiché (snapshot de ce qui est
    // parti au dernier dry-run). Toute réponse qui s'en écarte périme le
    // rapport : ses conséquences (ex. « pas le même élève » → la fiche TanoEd
    // devient sortante) n'y figurent pas, et la confirmation stricte du back
    // refuserait l'apply → re-dry-run obligatoire avant Valider. Le snapshot
    // sert aussi à EtapeRapport pour ajuster les compteurs sans compter double.
    const [resolutionsIntegrees, setResolutionsIntegrees] = useState({ conflits: {}, suspects: {}, deplacements: {} });
    const [sortantsCoches, setSortantsCoches] = useState(false);
    const [erreur, setErreur] = useState('');
    const [{ isLoading }, doPost] = usePostApi(url + 'eleves/reconcilier/?dry_run=true');

    const messageErreur = (reponse) =>
        reponse?.error?.response?.data?.error
        || 'Une erreur est survenue lors de la communication avec le serveur.';

    // Étape fichier → regroupement par classes reconnues, peuplées pré-cochées
    const handleDonnees = (lignes, nbSansClasse = 0) => {
        const g = grouperParClasses(lignes, etablissementData?.classes);
        setGroupes(g);
        setSansClasse(nbSansClasse);
        setCochees(new Set(g.filter((x) => x.nombreActuel > 0).map((x) => x.cle)));
        setErreur('');
        setEtape('classes');
    };

    const toggleClasse = (cle) => {
        setCochees((prev) => {
            const suivant = new Set(prev);
            if (suivant.has(cle)) suivant.delete(cle);
            else suivant.add(cle);
            return suivant;
        });
    };

    const toutCocher = (valeur) => {
        setCochees(valeur ? new Set(groupes.map((g) => g.cle)) : new Set());
    };

    const lancerDryRun = async (lignes, resolutions = null) => {
        const payload = JSON.stringify({
            etablissement_id: etablissementData.id,
            lignes,
            // Re-dry-run APRÈS arbitrages : les résolutions partent au moteur
            // pour que le rapport intègre leurs conséquences (la liste des
            // sortants notamment, dont l'apply strict exige les ids exacts).
            ...(resolutions ? {
                conflits_resolus: resolutions.conflits,
                suspects_resolus: resolutions.suspects,
                deplacements_resolus: creerSeulement(resolutions.deplacements),
            } : {}),
        });
        const reponse = await doPost(payload);
        if (reponse && reponse.success) {
            setRapport(reponse.data);
            if (!resolutions) {
                setResolutionsConflits({});
                setResolutionsSuspects({});
                setResolutionsDeplacements({});
            }
            setResolutionsIntegrees(resolutions
                ? { conflits: resolutions.conflits, suspects: resolutions.suspects, deplacements: resolutions.deplacements }
                : { conflits: {}, suspects: {}, deplacements: {} });
            setSortantsCoches(false);
            setErreur('');
            setEtape('rapport');
        } else {
            setErreur(messageErreur(reponse));
        }
    };

    // Re-vérification avec les réponses données (les conserve à l'écran).
    const reverifier = () => lancerDryRun(lignesEnvoyees, {
        conflits: resolutionsConflits,
        suspects: resolutionsSuspects,
        deplacements: resolutionsDeplacements,
    });

    // Rapport périmé : au moins une réponse s'écarte du snapshot intégré.
    // Comparaison SYMÉTRIQUE (une clé supprimée compte aussi) et '' ≡ absent
    // (« Je ne sais pas » et « sans réponse » sont équivalents pour le back).
    const rapportPerime = useMemo(() => {
        const differe = (a, b) => {
            const cles = new Set([...Object.keys(a), ...Object.keys(b)]);
            return [...cles].some((k) => (a[k] || '') !== (b[k] || ''));
        };
        return differe(resolutionsSuspects, resolutionsIntegrees.suspects)
            || differe(resolutionsConflits, resolutionsIntegrees.conflits)
            || differe(creerSeulement(resolutionsDeplacements), creerSeulement(resolutionsIntegrees.deplacements));
    }, [resolutionsSuspects, resolutionsConflits, resolutionsDeplacements, resolutionsIntegrees]);

    const analyser = async () => {
        const lignes = lignesPourMoteur(groupes, cochees);
        setLignesEnvoyees(lignes);
        await lancerDryRun(lignes);
    };

    const appliquer = async () => {
        const payload = JSON.stringify({
            etablissement_id: etablissementData.id,
            lignes: lignesEnvoyees,
            conflits_resolus: resolutionsConflits,
            suspects_resolus: resolutionsSuspects,
            deplacements_resolus: creerSeulement(resolutionsDeplacements),
            sortants_confirmes: rapport.sortants.map((s) => s.fiche_id),
        });
        let reponse;
        try {
            reponse = await doPost(payload, url + 'eleves/reconcilier/');
        } catch (e) {
            reponse = { success: false, error: e };
        }

        // L'écran de résultat est auto-suffisant (il n'affiche que la réponse de
        // l'apply). On NE rafraîchit PAS etablissementData ici : ce refresh
        // provoquerait un re-render de la page parente (et sa garde de rôle) qui
        // démonterait le wizard → écran de résultat perdu. La fraîcheur des
        // autres écrans (badge « en attente », effectifs) se met à jour au
        // retour sur un accueil (RootProf/RootChef rafraîchissent au montage).
        if (reponse && reponse.success) {
            setRapportFinal(reponse.data);
            setEchec(null);
            setErreur('');
            setEtape('resultat');
        } else {
            // Échec : écran de résultat DÉDIÉ (rouge), pas une bannière noyée
            setEchec(messageErreur(reponse));
            setRapportFinal(null);
            setEtape('resultat');
        }
    };

    const recommencer = () => {
        setEtape('fichier');
        setGroupes([]);
        setCochees(new Set());
        setSansClasse(0);
        setLignesEnvoyees([]);
        setRapport(null);
        setRapportFinal(null);
        setEchec(null);
        setResolutionsConflits({});
        setResolutionsSuspects({});
        setResolutionsDeplacements({});
        setResolutionsIntegrees({ conflits: {}, suspects: {}, deplacements: {} });
        setSortantsCoches(false);
        setErreur('');
    };

    const indexEtape = ETAPES.findIndex((e) => e.cle === etape);

    return (
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl shadow-blue-200 max-w-4xl w-full my-6 mx-auto relative overflow-hidden border border-white/50">
            {/* En-tête */}
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-700 via-blue-600 to-cyan-500" />
                <div className="relative px-6 py-6 flex items-center gap-3">
                    <div className="p-3 bg-white/20 backdrop-blur rounded-xl border border-white/30 shadow-xl">
                        <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-white">Import de l'établissement</h1>
                        <p className="text-white/80 text-sm">
                            {etablissementData?.nom}, mise à jour complète des élèves
                        </p>
                    </div>
                </div>
            </div>

            {/* Fil d'étapes */}
            <div className="px-6 pt-5">
                <div className="flex items-center gap-1 sm:gap-2">
                    {ETAPES.map((e, i) => (
                        <React.Fragment key={e.cle}>
                            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold ${
                                i === indexEtape
                                    ? 'bg-blue-600 text-white'
                                    : i < indexEtape ? 'text-blue-600' : 'text-slate-400'
                            }`}>
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
                                    i === indexEtape
                                        ? 'bg-white/25'
                                        : i < indexEtape ? 'bg-blue-100' : 'bg-slate-100'
                                }`}>
                                    {i + 1}
                                </span>
                                <span className="hidden sm:inline">{e.titre}</span>
                            </div>
                            {i < ETAPES.length - 1 ? (
                                <div className={`flex-1 h-px ${i < indexEtape ? 'bg-blue-300' : 'bg-slate-200'}`} />
                            ) : null}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* Contenu de l'étape */}
            <div className="px-6 py-6">
                {etape === 'fichier' ? (
                    <EtapeFichier onDonnees={handleDonnees} />
                ) : null}

                {etape === 'classes' ? (
                    <>
                        {erreur ? (
                            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3 mb-4 text-sm text-red-800">
                                {erreur}
                            </div>
                        ) : null}
                        <EtapeClasses
                            groupes={groupes}
                            cochees={cochees}
                            sansClasse={sansClasse}
                            onToggle={toggleClasse}
                            onTout={toutCocher}
                            listClasses={etablissementData?.classes}
                            limite={etablissementData?.limite_eleves}
                            onAnalyser={analyser}
                            onRetour={recommencer}
                            chargement={isLoading}
                        />
                    </>
                ) : null}

                {etape === 'rapport' && rapport ? (
                    <EtapeRapport
                        rapport={rapport}
                        lignes={lignesEnvoyees}
                        resolutionsConflits={resolutionsConflits}
                        onResoudreConflit={(cle, valeur) =>
                            setResolutionsConflits((prev) => ({ ...prev, [cle]: valeur }))}
                        resolutionsSuspects={resolutionsSuspects}
                        onResoudreSuspect={(paireId, valeur) =>
                            setResolutionsSuspects((prev) => {
                                // '' = retour à « sans réponse » : on retire la clé
                                const suivant = { ...prev };
                                if (valeur) suivant[paireId] = valeur;
                                else delete suivant[paireId];
                                return suivant;
                            })}
                        resolutionsDeplacements={resolutionsDeplacements}
                        onResoudreDeplacement={(ligne, valeur) =>
                            setResolutionsDeplacements((prev) => {
                                // '' = « plus tard » : on retire la clé
                                const suivant = { ...prev };
                                if (valeur) suivant[String(ligne)] = valeur;
                                else delete suivant[String(ligne)];
                                return suivant;
                            })}
                        sortantsCoches={sortantsCoches}
                        setSortantsCoches={setSortantsCoches}
                        rapportPerime={rapportPerime}
                        suspectsIntegres={resolutionsIntegrees.suspects}
                        onReverifier={reverifier}
                        onAppliquer={appliquer}
                        onRecommencer={recommencer}
                        onRetour={() => { setErreur(''); setEtape('classes'); }}
                        chargement={isLoading}
                        erreur={erreur}
                    />
                ) : null}

                {etape === 'resultat' && (rapportFinal || echec) ? (
                    <EtapeResultat
                        rapport={rapportFinal}
                        echec={echec}
                        onNouvelImport={recommencer}
                        onRetourRapport={() => { setEchec(null); setEtape('rapport'); }}
                    />
                ) : null}
            </div>
        </div>
    );
}

export default ImportWizardEtablissement;
